<template>
    <div class="book">
        <v-skeleton-loader v-if="loading" type="table"></v-skeleton-loader>
        <v-container v-if="!loading && data">
            <div>
                <v-text-field label="Search..."
                              prepend-icon="mdi-magnify"
                              v-model="searchTerm"
                              density="compact"
                              hint="Need at least 3 characters to search"
                              persistent-hint
                              clearable></v-text-field>
                <div class="mt-1">
                    <v-checkbox persistent-hint="true" label="Zero out all stock not included?" v-model="zeroOut" hint="Please tick the above if you are doing a full stocktake and want everything that is not included to be Zeroed out."></v-checkbox>
                </div>
            </div>
            <small>20Kg box packaging weight is approx 1.12kg</small>
            <table class="job-table">
                <thead class="header-repeat">
                    <tr class="name-row">
                        <th class="text-left">
                            COLOUR
                        </th>
                        <th style="width:10%" class="text-left">KG</th>
                        <th style="width:10%" class="text-left">BOXES</th>
                    </tr>
                </thead>
                <tbody v-for="colour in filteredData" :key="colour.id">
                    <tr>
                        <td class="text-left"><span v-html="formatSearchName(colour.selectName, searchTerm)"></span></td>
                        <td style="width:30%" class="text-left">
                            <v-text-field v-model="colour.quantity"
                                          density="compact"
                                          hide-details="true"
                                          @change="saveAsDraft(false)"
                                          suffix="kgs"></v-text-field>
                        </td>
                        <td style="width:30%" class="text-left">
                            <v-text-field v-model="colour.boxes"
                                          density="compact"
                                          @change="saveAsDraft(false)"
                                          hide-details="true"></v-text-field>
                        </td>
                    </tr>
                </tbody>
            </table>
            <v-card title="Stocktake Lines" text="All stocktake line changes" variant="tonal" class="mt-5">
                <v-card-text>
                    <v-alert v-if="zeroOut" class="my-2" text="Please ensure this is all the stock! Stocktake must be complete as a whole of all current stock. Anything not included in the list below will be set to 0." type="info"></v-alert>
                    <table class="job-table">
                        <thead class="header-repeat">
                            <tr class="name-row">
                                <th class="text-left">
                                    COLOUR
                                </th>
                                <th style="width:10%" class="text-left">KG</th>
                                <th style="width:10%" class="text-left">BOXES</th>
                            </tr>
                        </thead>
                        <tbody v-for="colour in stocktakeData" :key="colour.id">
                            <tr>
                                <td class="text-left"><span v-html="formatSearchName(colour.selectName, searchTerm)"></span></td>
                                <td style="width:10%" class="text-left">
                                    {{ colour.quantity }}
                                </td>
                                <td style="width:10%" class="text-left">
                                    {{ colour.boxes }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </v-card-text>

                <v-card-actions>
                    <v-btn @click="saveStocktake" :loading="saving" variant="tonal" color="success">Save Stocktake</v-btn>
                    <v-btn @click="saveAsDraft(true)" :loading="savingDraft" variant="outlined" color="primary" class="ml-2">Save as Draft</v-btn>
                    <small class="text-muted ml-4">This will update the stock in the background. Please allow 5mins for it to complete.</small>
                </v-card-actions>
            </v-card>
        </v-container>
    </div>
</template>
<script>
    import { mapActions, mapState } from "vuex";
    import { getTagClass } from '../mixins/jobMixins';
    import { numberWithCommas } from "../mixins/numberMixins";
    import { formatSearchName } from "../mixins/searchMixins";
    function initialState() {
        return {
            saving: false,
            savingDraft: false,
            loading: false,
            data: null,
            date: null,
            searchTerm: '',
            zeroOut: false
        };
    }
    export default {
        name: "StocktakeSheetView",
        data: function () {
            return initialState();
        },
        mounted: function () {
            this.date = this.$dayjs().format('YYYY-MM-DD');
            this.checkForDraft();
        },
        props: {
        },
        methods: {
            ...mapActions([
                "showErrorSnack","showSuccessSnack"
            ]),
            getTagClass,
            numberWithCommas,
            formatSearchName,
            reset: function () {
                Object.assign(this.$data, initialState());
            },
            checkForDraft() {
                const draftKey = 'stocktake_draft';
                const savedDraft = localStorage.getItem(draftKey);
                
                if (savedDraft) {
                    this.$confirm({
                        message: 'A saved draft was found. Would you like to load it?',
                        disableKeys: false,
                        auth: false,
                        button: {
                            no: 'No, Delete Draft',
                            yes: 'Yes, Load Draft'
                        },
                        callback: confirm => {
                            if (confirm) {
                                try {
                                    const parsedDraft = JSON.parse(savedDraft);
                                    this.data = parsedDraft.data;
                                    this.date = parsedDraft.date;
                                    this.zeroOut = parsedDraft.zeroOut;
                                    this.showSuccessSnack('Draft loaded successfully');
                                } catch (error) {
                                    console.error('Error parsing draft:', error);
                                    localStorage.removeItem(draftKey);
                                    this.showErrorSnack('Error loading draft, loading fresh data');
                                    this.loadData();
                                }
                            } else {
                                localStorage.removeItem(draftKey);
                                this.loadData();
                            }
                        }
                    });
                } else {
                    this.loadData();
                }
            },
            loadData() {
                this.loading = true;
                this.$api.get('colours/selectlist')
                    .then(res => {
                        this.data = res.data;
                        this.loading = false;
                    })
                    .catch(error => {
                        this.loading = false;
                        this.showErrorSnack('There was an issue getting the report');
                    });
            },
            saveAsDraft(redirect) {
                this.savingDraft = true;
                try {
                    const draftData = {
                        data: this.data,
                        date: this.date,
                        zeroOut: this.zeroOut,
                        savedAt: new Date().toISOString()
                    };
                    localStorage.setItem('stocktake_draft', JSON.stringify(draftData));
                    this.savingDraft = false;
                    if (redirect) {
                        this.showSuccessSnack('Draft saved successfully');
                        this.$router.push('/stock');
                    }
                } catch (error) {
                    this.savingDraft = false;
                    this.showErrorSnack('Error saving draft');
                    console.error('Draft save error:', error);
                }
            },
            saveStocktake() {
                var message = "ARE YOU SURE? THIS WILL UPDATE ONLY THE PROVIDED STOCK LINES.";
                if (this.zeroOut) {
                    message = `ARE YOU SURE THIS IS ALL THE STOCK? Any lines not included will be set to 0.`;
                }
                this.$confirm(
                    {
                        message: message,
                        disableKeys: false,
                        auth: false,
                        button: {
                            no: 'No',
                            yes: 'Yes'
                        },
                        callback: confirm => {
                            if (confirm) {
                                this.saving = true;
                                const stockLinesData = {
                                    stockLines: this.stocktakeData.map(line => ({
                                        id: line.id,
                                        quantity: line.quantity,
                                        boxes: line.boxes
                                    })),
                                    zeroOut: this.zeroOut
                                };
                                this.$api.post('stock/loadstocktakedata', stockLinesData, {
                                    headers: {
                                        'Content-Type': 'application/json'
                                    }
                                }).then(res => {
                                    this.saving = false;
                                    this.showSuccessSnack('Stock will be loaded shortly');
                                    // Clear the draft after successful save
                                    localStorage.removeItem('stocktake_draft');
                                    this.$router.push('/stock');
                                }).catch(error => {
                                    this.saving = false;
                                    this.showErrorSnack('There was an issue saving the stocktake!');
                                    console.error('Stocktake loading error:', error);
                                });
                                
                            }
                        }
                    }
                )
            }
        },
        computed: {
            filteredData() {
                if (this.data) {
                    if (this.searchTerm && this.searchTerm.length > 2) {
                        let upperSearch = this.searchTerm.toUpperCase();
                        return this.data.filter(item => {
                            return item.selectName.toUpperCase().includes(upperSearch);
                        }).splice(0, 10);
                    }
                }
                return [];
            },
            stocktakeData() {
                if (this.data) {
                    return this.data.filter(item => {
                        return item.quantity || item.boxes;
                    });
                }
                return [];
            }
        }
    };
</script>
<style scoped lang="scss">
    .buttons {
        width: 100%;
        position: fixed;
        top: 0;
        background-color: #FAFAFA;
        border-bottom: 1px #D3D3D3 solid;
        z-index: 10;
    }

    .button-controls {
        padding-top: 20px;
        width: 287mm;
        margin-left: auto;
        margin-right: auto;
        padding-bottom: 20px;
    }

    :deep(.v-textarea textarea) {
        font-size: 12px !important;
    }

    .job-table {
        border-collapse: collapse;
        width: 100%;
        border: none !important;
        margin-top:10px;
    }

    .job-table, .job-table td, th {
        border: 1px black solid;
        font-size: 12px;
        padding: 3px;
    }

    body {
        width: 100%;
        height: 100%;
        margin: 0;
        padding: 0;
        background-color: #FAFAFA;
        font: 12pt "Tahoma";
    }

    .name-row td, th {
        box-shadow: inset -1px -1px 100px 100px #212121;
        font-weight: bold;
        color: white;
    }

    * {
        box-sizing: border-box;
        -moz-box-sizing: border-box;
    }

    .book {
        margin-top: 80px;
    }

    .page {
        width: 210mm;
        min-height: 287mm;
        padding: 10mm;
        margin: 10mm auto;
        padding-top: 2.5mm;
        margin-top: 5mm;
        border: 1px #D3D3D3 solid;
        border-radius: 5px;
        background: white;
        box-shadow: 0 0 5px rgba(0, 0, 0, 0.1);
    }

    .subpage {
        padding: 0.5cm;
        padding-top: 0.25cm;
        width: 190mm;
        height: auto;
        position: relative;
        display: table;
        table-layout: fixed;
    }
    @page {
        size: A4;
        margin: 0;
    }

    @media print {
        @page {
            size: A4 portrait;
            margin-top: 15mm !important;
            margin-right: 5mm !important;
            margin-bottom: 5mm !important;
            margin-left: 5mm !important;
        }
        .subpage {
            page-break-before: auto;
        }

        .no-border {
            border: none !important;
            height:10mm;
        }

        .footer-repeat {
            display: table-footer-group;
            border: none !important;
        }

        .header-repeat {
            display: table-header-group;
            border: none !important;
            padding-top: 5mm;
        }

        .job-table {
            margin-top: 5mm;
        }

        .bg-orange-lighten-4 {
            box-shadow: inset -1px -1px 100px 100px #ffe0b2;
        }

        .bg-orange-darken-1 {
            box-shadow: inset -1px -1px 100px 100px #fb8c00;
        }

        .bg-red-lighten-4 {
            box-shadow: inset -1px -1px 100px 100px #FFCDD2;
        }

        .bg-red-darken-1 {
            box-shadow: inset -1px -1px 100px 100px #E53935;
        }

        @page {
            size: portrait A4;
        }

        html, body {
            height: 210mm;
            width: 297mm;
        }

        .book {
            margin-top: 0px;
        }

        .buttons {
            display: none;
        }

        :deep(.v-textarea textarea) {
            overflow: hidden;
        }

        .page {
            margin: 0;
            border: initial;
            border-radius: initial;
            width: initial;
            min-height: initial;
            box-shadow: initial;
            background: initial;
            page-break-after: always;
        }
    }
</style>